import { useCreateInvitation, useDeleteDealUser } from '@/api/accounting-dealer'
import { useCompany } from '@/context/companies'
import { Add, Delete } from '@mui/icons-material'
import {
  Button,
  Chip,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material'
import {
  AddCollaboratorDialog,
  AddCollaboratorFormData,
} from './add-collaborator-dialog'
import { Fragment, useState } from 'react'
import { toast } from '@/helpers'
import { useQueryClient } from '@tanstack/react-query'
import { RemoveCollaboratorDialog } from './remove-collaborator-dialog'
import { RemoveInvitationDialog } from './remove-invitation-dialog'
import { PageHeader } from '@/components'
import { routes } from '@/router'

export function CollaboratorsPage() {
  const { deal } = useCompany()

  const queryClient = useQueryClient()

  const [isAddCollaboratorDialogOpen, setIsAddCollaboratorDialogOpen] =
    useState(false)
  const [isRemoveCollaboratorDialogOpen, setIsRemoveCollaboratorDialogOpen] =
    useState(false)
  const [isRemoveInvitationDialogOpen, setIsRemoveInvitationDialogOpen] =
    useState(false)

  const [selectedDealUserKey, setSelectedDealUserKey] = useState<string | null>(
    null,
  )

  const invitations = deal.deal_users.filter(
    (dealUser) => dealUser.profile !== 'admin',
  )

  const { mutate: createInvitation, isPending: isCreatingInvitation } =
    useCreateInvitation({
      onSuccess: () => {
        toast.success('Convite enviado com sucesso!')
        invalidateDealsQuery()
      },
      onError: (error) => {
        toast.error(error.message)
      },
      onSettled: () => {
        setIsAddCollaboratorDialogOpen(false)
      },
    })

  const { mutate: deleteDealUser, isPending: isDeletingDealUser } =
    useDeleteDealUser({
      onSuccess: () => {
        toast.success('Convite removido.')
        invalidateDealsQuery()
      },
      onError: (error) => {
        toast.error(error.message)
      },
      onSettled: () => {
        setIsRemoveInvitationDialogOpen(false)
        setIsRemoveCollaboratorDialogOpen(false)
      },
    })

  const invalidateDealsQuery = () => {
    queryClient.invalidateQueries({
      queryKey: ['deals'],
    })
  }

  const toggleAddCollaboratorDialog = () =>
    setIsAddCollaboratorDialogOpen((prev) => !prev)

  const toggleRemoveCollaboratorDialog = () =>
    setIsRemoveCollaboratorDialogOpen((prev) => !prev)

  const toggleRemoveInvitationDialog = () =>
    setIsRemoveInvitationDialogOpen((prev) => !prev)

  const handleAddCollaborator = (data: AddCollaboratorFormData) => {
    createInvitation({
      deal_key: deal.key,
      name: data.name,
      tax_id: data.tax_id,
      email: data.email,
      phone: data.phone,
    })
  }

  const handleRemoveDealUser = () => {
    if (!selectedDealUserKey) return

    deleteDealUser({
      deal_key: deal.key,
      user_key: selectedDealUserKey,
    })
  }

  return (
    <>
      <PageHeader rollbackRoute={routes.menu}>Colaboradores</PageHeader>

      {!invitations.length ? (
        <Typography color="gray" textAlign="center" my={2}>
          Nenhum colaborador encontrado.
        </Typography>
      ) : (
        <List sx={{ '& .MuiDivider-root:last-child': { display: 'none' } }}>
          {invitations?.map(({ email, key, status, user_key }) => (
            <Fragment key={key}>
              <ListItem disableGutters>
                <ListItemText
                  primary={email}
                  primaryTypographyProps={{ noWrap: true }}
                  secondary="Remover convite"
                  secondaryTypographyProps={{
                    display: status === 'pending' ? 'inline-block' : 'none',
                    component: Link,
                    color: 'error',
                    onClick: () => {
                      setSelectedDealUserKey(user_key)
                      toggleRemoveInvitationDialog()
                    },
                    sx: (theme) => ({
                      textDecorationColor: theme.palette.error.main,
                    }),
                  }}
                  sx={{ m: 0 }}
                />

                {status === 'pending' ? (
                  <Chip label="Pendente" color="warning" size="small" />
                ) : status === 'expired' ? (
                  <Chip label="Expirado" color="error" size="small" />
                ) : (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedDealUserKey(user_key)
                      toggleRemoveCollaboratorDialog()
                    }}
                  >
                    <Delete />
                  </IconButton>
                )}
              </ListItem>

              <Divider />
            </Fragment>
          ))}
        </List>
      )}

      <Button
        variant="outlined"
        startIcon={<Add />}
        sx={{ mt: 2 }}
        onClick={toggleAddCollaboratorDialog}
      >
        Convidar colaborador
      </Button>

      <AddCollaboratorDialog
        open={isAddCollaboratorDialogOpen}
        onClose={toggleAddCollaboratorDialog}
        onAddCollaborator={handleAddCollaborator}
        isLoading={isCreatingInvitation}
      />

      <RemoveInvitationDialog
        open={isRemoveInvitationDialogOpen}
        onClose={toggleRemoveInvitationDialog}
        onRemoveInvitation={handleRemoveDealUser}
        isLoading={isDeletingDealUser}
      />

      <RemoveCollaboratorDialog
        open={isRemoveCollaboratorDialogOpen}
        onClose={toggleRemoveCollaboratorDialog}
        onRemoveCollaborator={handleRemoveDealUser}
        isLoading={isDeletingDealUser}
      />
    </>

    // <Card variant="outlined">
    //   <Typography variant="h5">Colaboradores</Typography>

    //   {readingInvitations ? (
    //     <Box display="flex" justifyContent="center" alignItems="center">
    //       <CircularProgress size={24} />
    //     </Box>
    //   ) : invitations?.length === 0 ? (
    //     <Typography color="gray" variant="body2" my={2}>
    //       Você não possui nenhum colaborador.
    //     </Typography>
    //   ) : (
    //     <List sx={{ '& .MuiDivider-root:last-child': { display: 'none' } }}>
    //       {invitations?.map(({ email, key, status, user_id }) => (
    //         <Fragment key={key}>
    //           <ListItem key={key} disableGutters>
    //             <ListItemText
    //               primary={email}
    //               primaryTypographyProps={{ noWrap: true }}
    //               secondary="Remover convite"
    //               secondaryTypographyProps={{
    //                 display: status === 'pending' ? 'inline-block' : 'none',
    //                 component: Link,
    //                 color: 'error',
    //                 onClick: () => {
    //                   setSelectedInvitation(key)
    //                   toggleRemoveInvitationDialog()
    //                 },
    //                 sx: (theme) => ({
    //                   textDecorationColor: theme.palette.error.main,
    //                 }),
    //               }}
    //               sx={{ m: 0 }}
    //             />

    //             {status === 'pending' ? (
    //               <Chip label="Pendente" color="warning" size="small" />
    //             ) : status === 'expired' ? (
    //               <Chip label="Expirado" color="error" size="small" />
    //             ) : (
    //               <IconButton
    //                 size="small"
    //                 onClick={() => {
    //                   setSelectedCollaborator(user_id)
    //                   toggleRemoveCollaboratorDialog()
    //                 }}
    //               >
    //                 <Delete />
    //               </IconButton>
    //             )}
    //           </ListItem>

    //           <Divider />
    //         </Fragment>
    //       ))}
    //     </List>
    //   )}
    //   <Button
    //     variant="outlined"
    //     size="small"
    //     startIcon={<Add />}
    //     sx={{ mt: 1 }}
    //     onClick={toggleAddCollaboratorDialog}
    //   >
    //     Convidar colaborador
    //   </Button>

    //   <AddCollaboratorDialog
    //     open={isAddCollaboratorDialogOpen}
    //     onClose={toggleAddCollaboratorDialog}
    //     onAddCollaborator={handleAddCollaborator}
    //     isLoading={isCreatingInvitation}
    //   />

    //   <RemoveCollaboratorDialog
    //     open={isRemoveCollaboratorDialogOpen}
    //     onClose={toggleRemoveCollaboratorDialog}
    //     onRemoveCollaborator={handleRemoveCollaborator}
    //     isLoading={isRemovingCollaborator}
    //   />

    //   <RemoveInvitationDialog
    //     open={isRemoveInvitationDialogOpen}
    //     onClose={toggleRemoveInvitationDialog}
    //     onRemoveInvitation={handleRemoveInvitation}
    //     isLoading={isRemovingInvitation}
    //   />
    // </Card>
  )
}
